/**
 * Org-admin connect contracts for GitLab / Bitbucket PAT integrations.
 * Credentials are write-only — list/get responses never include tokens or webhook secrets.
 */
import { z } from 'zod';
import { SOURCE_REPO_ACCESS_MODES } from './execution-jobs';
import { SOURCE_REPO_KINDS } from './source-repos';

export const GitlabIntegrationPublicSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  gitlabHost: z.string().min(1).max(300),
  projectPath: z.string().min(1).max(500),
  suspended: z.boolean(),
  hasWebhookSecret: z.boolean(),
  installedByUserId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  defaultBranch: z.string().max(200).nullable().optional(),
});
export type GitlabIntegrationPublic = z.infer<typeof GitlabIntegrationPublicSchema>;

export const BitbucketIntegrationPublicSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  workspace: z.string().min(1).max(200),
  repoSlug: z.string().min(1).max(200),
  suspended: z.boolean(),
  hasWebhookSecret: z.boolean(),
  installedByUserId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  defaultBranch: z.string().max(200).nullable().optional(),
});
export type BitbucketIntegrationPublic = z.infer<typeof BitbucketIntegrationPublicSchema>;

export const ConnectGitlabIntegrationRequestSchema = z.object({
  orgId: z.string().uuid(),
  gitlabHost: z.string().min(1).max(300).optional(),
  projectPath: z
    .string()
    .min(1)
    .max(500)
    .regex(/^[\w.-]+(?:\/[\w.-]+)+$/, 'projectPath must be namespace/project (slashes allowed)'),
  accessToken: z.string().min(8).max(2000),
  webhookSecret: z.string().min(8).max(500).optional(),
  /** When set, also bind `project_source_repos` for this Rynora project. */
  projectId: z.string().uuid().optional(),
  kind: z.enum(SOURCE_REPO_KINDS).optional(),
  accessMode: z.enum(SOURCE_REPO_ACCESS_MODES).optional(),
});
export type ConnectGitlabIntegrationRequest = z.infer<typeof ConnectGitlabIntegrationRequestSchema>;

export const ConnectBitbucketIntegrationRequestSchema = z.object({
  orgId: z.string().uuid(),
  workspace: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[\w-]+$/, 'workspace must be a Bitbucket workspace slug'),
  repoSlug: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[\w.-]+$/, 'repoSlug must be a Bitbucket repository slug'),
  accessToken: z.string().min(8).max(2000),
  webhookSecret: z.string().min(8).max(500).optional(),
  projectId: z.string().uuid().optional(),
  kind: z.enum(SOURCE_REPO_KINDS).optional(),
  accessMode: z.enum(SOURCE_REPO_ACCESS_MODES).optional(),
});
export type ConnectBitbucketIntegrationRequest = z.infer<typeof ConnectBitbucketIntegrationRequestSchema>;

export const ConnectGitlabIntegrationResponseSchema = z.object({
  integration: GitlabIntegrationPublicSchema,
  sourceRepoId: z.string().uuid().nullable(),
  webhookHint: z.string().max(500),
});
export type ConnectGitlabIntegrationResponse = z.infer<typeof ConnectGitlabIntegrationResponseSchema>;

export const ConnectBitbucketIntegrationResponseSchema = z.object({
  integration: BitbucketIntegrationPublicSchema,
  sourceRepoId: z.string().uuid().nullable(),
  webhookHint: z.string().max(500),
});
export type ConnectBitbucketIntegrationResponse = z.infer<typeof ConnectBitbucketIntegrationResponseSchema>;

export const ListGitlabIntegrationsResponseSchema = z.object({
  orgId: z.string().uuid(),
  integrations: z.array(GitlabIntegrationPublicSchema),
});
export type ListGitlabIntegrationsResponse = z.infer<typeof ListGitlabIntegrationsResponseSchema>;

export const ListBitbucketIntegrationsResponseSchema = z.object({
  orgId: z.string().uuid(),
  integrations: z.array(BitbucketIntegrationPublicSchema),
});
export type ListBitbucketIntegrationsResponse = z.infer<typeof ListBitbucketIntegrationsResponseSchema>;
